import * as React from "react";

// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type { Button, IButton, IKeytipProps } from "@fluentui/react";
// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import { BaseButton } from "@fluentui/react";
import type { ITheme } from "@fluentui/react/lib/Styling";

import { getInputMethodFromEvent, InputMethod, isIE } from "@1js/acui-common";
import { AppThemeConsumer } from "@1js/acui-theme";

import type { AppBaseButtonProps } from "./AppBaseButton.Props";
import { getAppBaseButtonStyles } from "./AppBaseButton.styles";
import { ReactUiPaintTiming } from "@1js/acui-paint-timing";
import { AppContext } from "@1js/acui-context";
import { checkExperiment, ribbonExperiments } from "@1js/acui-experiments";

export class AppBaseButton extends React.PureComponent<AppBaseButtonProps, {}> {
  private button = React.createRef<IButton>();
  private buttonRef = React.createRef<HTMLElement>();
  private pointerDownEventId: number | undefined = undefined;
  private pointerInputMethod: InputMethod | undefined = undefined;

  // This allows us to access AppContext directly from this.context
  static contextType: any = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  public render() {
    const shouldTrackAsyncUI = checkExperiment(
      this.context,
      ribbonExperiments.trackOOUXAsyncUiEnabled
    );
    return (
      <AppThemeConsumer>
        {(theme: ITheme) => (
          <>
            <BaseButton
              elementRef={this.buttonRef}
              {...this.props}
              keytipProps={this.props.keytipProps as IKeytipProps}
              componentRef={this.button}
              onMouseDown={this.onMouseDown}
              styles={getAppBaseButtonStyles(
                theme,
                this.props.styles,
                this.props.iconProps !== undefined
              )}
              allowDisabledFocus={true}
              onPointerDown={this.onPointerDown}
              onPointerUp={this.onPointerUp}
            />
            {shouldTrackAsyncUI && (
              <ReactUiPaintTiming
                elementTimingId={"AppBaseButton"}
                hidden={
                  !(this.button?.current as BaseButton)?.state
                    ? true
                    : (this.button.current as BaseButton).state.menuHidden
                }
                interactivityTrackerContext={undefined}
              />
            )}
          </>
        )}
      </AppThemeConsumer>
    );
  }

  public get buttonElement(): HTMLElement | null {
    return this.buttonRef.current;
  }

  public openMenu = (
    shouldFocusOnContainer?: boolean,
    shouldFocusOnMount?: boolean
  ) => {
    if (this.button.current) {
      this.button.current.openMenu(shouldFocusOnContainer, shouldFocusOnMount);
    }
  };

  public dismissMenu = () => {
    if (this.button.current) {
      this.button.current.dismissMenu();
    }
  };

  public focus = () => {
    this.button.current && this.button.current.focus();
  };

  public getInputMethod = (
    ev?: React.MouseEvent<Button>
  ): InputMethod | undefined => {
    let inputMethod = this.pointerInputMethod;

    // if inputMethod is not Touch or Pen and we have a mouse event, lets attempt to get
    // the input method from event instead of the pointer.  In cases where the event is undefined
    // we will always use pointer.  For example, flyout anchors don't pass an event.
    // However, IE will have ev.native always as 0 which will make us set
    // inputMethod for clicks to Keyboard, we know this is a mouse click so lets just return
    // the correct inputMethod
    if (
      ev &&
      inputMethod !== InputMethod.Pen &&
      inputMethod !== InputMethod.Touch &&
      !(isIE() && inputMethod === InputMethod.Mouse)
    ) {
      inputMethod = getInputMethodFromEvent(ev);
    }

    this.pointerInputMethod = undefined;
    return inputMethod;
  };

  private onMouseDown = (ev: React.MouseEvent<BaseButton>) => {
    if (this.props.onMouseDown) {
      this.props.onMouseDown(ev);
    }

    if (!this.props.takeFocusOnClick) {
      ev.preventDefault();
    }
  };

  private onPointerDown = (ev: React.PointerEvent<HTMLElement>) => {
    this.pointerDownEventId = ev.pointerId;
  };

  private onPointerUp = (ev: React.PointerEvent<HTMLElement>) => {
    if (this.pointerDownEventId === ev.pointerId) {
      switch (ev.pointerType) {
        case "touch":
          this.pointerInputMethod = InputMethod.Touch;
          break;
        case "pen":
          this.pointerInputMethod = InputMethod.Pen;
          break;
        case "mouse":
          this.pointerInputMethod = InputMethod.Mouse;
          break;
        default:
          this.pointerInputMethod = InputMethod.Unknown;
      }
    }
    this.pointerDownEventId = undefined;
  };
}
